import { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Menu, X } from "lucide-react";

import { fa } from "@/locales/fa";
import { cn } from "@/lib/utils";
import { navItems } from "./Sidebar";

/**
 * Mobile navigation. The desktop <Sidebar> is `hidden md:flex`, so below the
 * md breakpoint there was previously no way to move between pages at all.
 * This renders a hamburger button (only `md:hidden`) that opens a slide-in
 * drawer carrying the exact same nav list as the sidebar.
 *
 * Built on the raw Radix Dialog primitive rather than our ui/dialog wrapper
 * because that wrapper hard-codes a centered modal; a nav drawer needs to be
 * pinned to the inline-start edge and slide in from there. In RTL that edge
 * is the right side, which `start-0` + a translate handles automatically.
 */
export function MobileNav() {
  const [open, setOpen] = useState(false);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={fa.nav.menu}
          className="md:hidden -ms-1 grid place-items-center size-9 rounded-md text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors"
        >
          <Menu className="size-5" />
        </button>
      </DialogPrimitive.Trigger>

      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 md:hidden" />
        <DialogPrimitive.Content
          className={cn(
            "fixed inset-y-0 start-0 z-50 flex w-72 max-w-[85vw] flex-col bg-card border-e border-border shadow-xl md:hidden",
            "duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right",
          )}
        >
          <DialogPrimitive.Title className="sr-only">{fa.nav.menu}</DialogPrimitive.Title>

          <div className="flex items-center justify-between gap-2 h-14 px-4 border-b border-border">
            <Link
              to="/"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5"
              aria-label={fa.appShort}
            >
              <div className="grid place-items-center size-8 rounded-lg bg-white ring-1 ring-border shadow-sm">
                <img src="/logo.webp" alt={fa.appShort} className="size-6 object-contain" draggable={false} />
              </div>
              <span className="text-sm font-semibold tracking-tight">{fa.appShort}</span>
            </Link>
            <DialogPrimitive.Close
              className="grid place-items-center size-8 rounded-md text-muted-foreground hover:bg-muted/60 hover:text-foreground transition-colors"
              aria-label={fa.common.close}
            >
              <X className="size-4" />
            </DialogPrimitive.Close>
          </div>

          <nav className="flex-1 overflow-y-auto px-3 py-4">
            <ul className="space-y-0.5">
              {navItems.map(({ to, icon: Icon, label, group }, idx) => {
                const prev = navItems[idx - 1];
                const showDivider = group === "ops" && prev?.group !== "ops";
                return (
                  <li key={to}>
                    {showDivider && <div className="mx-2 my-3 h-px bg-border" aria-hidden />}
                    <NavLink
                      to={to}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors",
                          isActive
                            ? "bg-muted text-foreground font-medium"
                            : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                        )
                      }
                    >
                      <Icon className="size-[16px] shrink-0" strokeWidth={1.75} />
                      <span>{label}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
